import {
  commendaAiConversationsKey,
  listCommendaAiConversations,
} from '@commenda/api/commenda-ai/api';
import { useConfig } from 'context/useConfig';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import useSWR from 'swr';

import type { TSidebarRecentItem } from '../Layouts/Layout/CommendaNavbar';
import { AffiliateAiPromptHelper } from './AffiliateAiPromptHelper';
import { useAffiliateAiClient } from './useAffiliateAiClient';

interface UseAffiliateChatRecentsOptions {
  /** The resolved A_AND_M_TP_DEMO flag, passed down by the navigation so this hook (which runs on
   * every affiliate page) fires no firm or conversation fetches while the real chat is off. */
  enabled: boolean;
}

/** Real conversations for the affiliate navbar's Recents list, following the chat's scope: the
 * member's firm-scope conversations in all-clients mode, the picked client's otherwise. Each href
 * carries the scope it belongs to, so a recent always reopens where it can load. The list waits for
 * the firm to resolve so it never fetches the firm list for a client the URL will settle on. */
export const useAffiliateChatRecents = ({
  enabled,
}: UseAffiliateChatRecentsOptions): {
  items: TSidebarRecentItem[];
  isLoading: boolean;
  error: unknown;
} => {
  const router = useRouter();
  const { affiliateFirmId } = useConfig();
  const {
    clientId,
    scope,
    isLoading: isClientLoading,
    error: clientError,
  } = useAffiliateAiClient({
    enabled,
  });
  const listScope = scope === 'firm' ? 'firm' : undefined;

  const {
    data: conversations = [],
    isLoading: isListLoading,
    error: listError,
  } = useSWR(
    enabled && !isClientLoading && !clientError
      ? commendaAiConversationsKey(clientId, true, listScope)
      : null,
    ([, workspaceId, keyScope]) =>
      listCommendaAiConversations(workspaceId, keyScope)
  );
  const isLoading = enabled && (isClientLoading || isListLoading);
  const error = enabled ? (clientError ?? listError) : undefined;

  const activeId =
    typeof router.query.c === 'string' ? router.query.c : undefined;
  const isChatSurface = router.pathname.includes('/commenda-ai');

  return useMemo(() => {
    if (!enabled || !affiliateFirmId)
      return { items: [], isLoading: false, error: undefined };
    const clientParamValue =
      clientId === null
        ? AffiliateAiPromptHelper.ALL_CLIENTS_PARAM_VALUE
        : String(clientId);

    return {
      items: conversations.map((conversation) => ({
        key: conversation.id,
        label: conversation.title ?? 'Untitled chat',
        href: `/affiliate/${affiliateFirmId}/commenda-ai?${AffiliateAiPromptHelper.CLIENT_QUERY_PARAM}=${clientParamValue}&c=${conversation.id}`,
        isActive: isChatSurface && activeId === conversation.id,
      })),
      isLoading,
      error,
    };
  }, [
    enabled,
    affiliateFirmId,
    clientId,
    conversations,
    isChatSurface,
    activeId,
    isLoading,
    error,
  ]);
};
